import type { Municipality, TransactionDetails } from '@/types';
import { isPlottable, type PlottableMunicipality } from './transactionLocationMapModel';

/**
 * The two locations on a transaction (VER-344), and — since VER-358 — the single
 * presentation each combination gets.
 *
 * A transaction has a LOCATION — the city the card descriptor printed, the one source that
 * testifies where the cardholder was. A merchant has an ADDRESS — where the company is
 * registered. The API already keeps them apart (`purchaseLocation` is sent only for
 * descriptor-sourced geo), so nothing here has to second-guess provenance: if a purchase
 * location arrived, the cardholder was there.
 *
 * The contract is ONE PRESENTATION PER FACT: a municipality is printed exactly once in the
 * drawer — as the purchase line, as a label on the relation map, or as the company's seat
 * inside "Estabelecimento" — never twice.
 */
export type GeoPresentation =
  /** Nothing to say — no row at all. Absence is what keeps a platform's own city from
   *  reading as where the user bought (VER-356). */
  | { kind: 'none' }
  /** Only the company's registered seat is known: it renders inside "Estabelecimento",
   *  never as a purchase. */
  | { kind: 'address-only'; address: Municipality }
  /** Only the descriptor city is known. */
  | { kind: 'purchase-only'; purchase: Municipality }
  /** Purchase and CNPJ in the same municipality — one line says both. */
  | { kind: 'same'; purchase: Municipality }
  /** Both known and different, both plottable: the relation map presents them. `warn`
   *  marks the contradiction class — too far apart AND a CNAE that cannot have sold this
   *  in person — where the likely truth is a wrong identity. */
  | {
      kind: 'relation';
      purchase: PlottableMunicipality;
      address: PlottableMunicipality;
      km: number;
      warn: boolean;
    }
  /** Both known and different but at least one has no centroid — no RELATION to draw or
   *  measure, so each fact renders on its own: the purchase as the single-point map when
   *  plottable (text otherwise), the address as text inside "Estabelecimento". */
  | { kind: 'split'; purchase: Municipality; address: Municipality };

/** Haversine over two municipality centroids — the only coordinates the pipeline holds. */
export function haversineKm(
  a: Pick<PlottableMunicipality, 'lat' | 'lng'>,
  b: Pick<PlottableMunicipality, 'lat' | 'lng'>,
): number {
  const R = 6371;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * CNAE classes that cannot plausibly have SOLD the purchase in person — condominium
 * administration (8112), real-estate rental/management (6810, 6822). Distance alone is a
 * branch network; distance plus one of these is the Villa Castelli class: a wrong
 * identity, not a road trip. Encodes the failure CLASS, not the instance.
 */
const CNAE_IMPLAUSIBLE_SELLER_PREFIXES = ['8112', '6810', '6822'] as const;

export function cnaeIsImplausibleSeller(cnae: string | null | undefined): boolean {
  const head = (cnae ?? '').replace(/\D/g, '').slice(0, 4);
  return (CNAE_IMPLAUSIBLE_SELLER_PREFIXES as readonly string[]).includes(head);
}

/** Above this, a purchase-vs-CNPJ gap stops reading as "drove to the next town". */
export const GEO_WARN_KM = 150;

export function buildGeoPresentation(details?: TransactionDetails | null): GeoPresentation {
  const purchase = details?.purchaseLocation;
  const address = details?.establishment?.address;

  if (!purchase && !address) return { kind: 'none' };
  if (!purchase) return { kind: 'address-only', address: address! };
  if (!address) return { kind: 'purchase-only', purchase };
  if (purchase.ibgeCode === address.ibgeCode) return { kind: 'same', purchase };
  if (!isPlottable(purchase) || !isPlottable(address)) return { kind: 'split', purchase, address };

  const km = haversineKm(purchase, address);
  const warn = km > GEO_WARN_KM && cnaeIsImplausibleSeller(details?.establishment?.cnae);
  return { kind: 'relation', purchase, address, km, warn };
}

/**
 * Whether the company's seat still needs a line inside "Estabelecimento". True only when
 * the address was not already presented elsewhere (map label or "same" line). A type
 * predicate so the caller can read `geo.address` under it.
 */
export function addressBelongsInEstablishment(
  geo: GeoPresentation,
): geo is Extract<GeoPresentation, { kind: 'address-only' | 'split' }> {
  return geo.kind === 'address-only' || geo.kind === 'split';
}
